import PropTypes from "prop-types";
import { memo, useMemo, useRef } from "react";

import CanvasjsBarChart from "@/components/Charts/CanvasjsBarChart";
import CanvasjsBarLineComboChart from "@/components/Charts/CanvasjsBarLineComboChart";
import CanvasjsPieChart from "@/components/Charts/CanvasPieChart";
import ChartJsBarChart from "@/components/Charts/ChartJsBarChart";
import ChartJsLineChart from "@/components/Charts/ChartJsLineChart";
import SplineChart from "@/components/Charts/SplineChart";
import LoadingOverlay from "@/components/Shared/LoadingOverlay";
import { useGetChartDataQuery } from "@/redux/api/dashboardApi";
import { getDashboardFilterDate } from "@/utils/globalUtils";
import { useSelector } from "react-redux";
import ChartJsBarLineCombo from "../Charts/ChartJsBarLineCombo";

const ChartFetch = memo(({ data, type, isDark: isDarkProps, dashboardName }) => {
  const ref = useRef(null);
  // const { isFullScreen } = useSelector((state) => state.theme);
  const theme = useSelector((state) => state.theme.isDark);
  const isDark = isDarkProps === undefined ? theme : isDarkProps;
  const { dateRange } = useSelector((state) => state.dashboardV2);

  const {
    component,
    chartTitle,
    locations = {},
    comboLocations = {},
    isTitleHidden,
    showThumbsUp,
    chartType,
    comparisonPeriods = [],
    window = "",

    interval,
    operator,
    lastXDaysValue,
    lastXDays,
    xAxis,
    timeRange = [],

    barInterval,
    barOperator,
    barLastXDaysValue,
    barLastXDays,
    barXAxis,
    barTimeRange,

    lineInterval,
    lineOperator,
    lineLastXDaysValue,
    lineLastXDays,
    lineXAxis,
    lineTimeRange,
    refetch,
  } = data;

  const { params, lineParams, barParams } = useMemo(() => {
    const baseParams = {
      operator,
      peakHours: dateRange.peakHours,
      refetch,
      window,
      dashboardName,
      // comparisonPeriods: Array.isArray(comparisonPeriods) ? JSON.stringify(comparisonPeriods) : [comparisonPeriods],
    };

    let queryParams = null;
    let queryLineParams = null;
    let queryBarParams = null;

    if (component === "comboBar") {
      const lineDate = getDashboardFilterDate({
        xAxis: lineXAxis,
        lastXDaysValue: lineLastXDaysValue,
        lastXDays: lineLastXDays,
        timeRange: lineTimeRange,
      });
      const barDate = getDashboardFilterDate({
        xAxis: barXAxis,
        lastXDaysValue: barLastXDaysValue,
        lastXDays: barLastXDays,
        timeRange: barTimeRange,
      });
      const { line, bar } = comboLocations || {};

      const lineDeviceParameter = JSON.stringify({
        _id_in: (Array.isArray(line?.yAxis) ? line.yAxis : []).join(","),
        populate: "systemFormula systemParameter",
        limit: 100,
      });
      const barDeviceParameter = JSON.stringify({
        _id_in: (Array.isArray(bar?.yAxis) ? bar.yAxis : []).join(","),
        populate: "systemFormula systemParameter",
        limit: 100,
      });

      queryLineParams = { ...baseParams, ...lineDate, deviceParameter: lineDeviceParameter, interval: lineInterval, operator: lineOperator };
      queryBarParams = { ...baseParams, ...barDate, deviceParameter: barDeviceParameter, interval: barInterval, operator: barOperator };
    } else {
      const deviceParameter = JSON.stringify({
        _id_in: (Array.isArray(locations?.yAxis) ? locations.yAxis : [locations.yAxis]).join(","),
        populate: "systemFormula systemParameter",
        limit: 100,
      });
      const date = getDashboardFilterDate({ xAxis, lastXDaysValue, lastXDays, timeRange, comparisonPeriods, window });
      queryParams = { ...baseParams, ...date, deviceParameter, interval, operator };
    }
    return { params: queryParams, lineParams: queryLineParams, barParams: queryBarParams };
  }, [
    operator,
    component,
    lineXAxis,
    lineLastXDaysValue,
    lineLastXDays,
    lineTimeRange,
    barXAxis,
    barLastXDaysValue,
    barLastXDays,
    barTimeRange,
    comboLocations,
    lineInterval,
    lineOperator,
    barInterval,
    barOperator,
    locations,
    xAxis,
    lastXDaysValue,
    lastXDays,
    timeRange[0],
    timeRange[1],
    interval,
    refetch,
    comparisonPeriods,
  ]);

  // console.log({ timeRange, params });

  const { data: fetchedGraphData, isFetching: isGraphFetching } = useGetChartDataQuery(params, { skip: !params });
  const { data: fetchedLineData, isFetching: isLineFetching } = useGetChartDataQuery(lineParams, { skip: !lineParams });
  const { data: fetchedBarData, isFetching: isBarFetching } = useGetChartDataQuery(barParams, { skip: !barParams });

  const graphData = fetchedGraphData?.data || [];
  const lineGraphData = fetchedLineData?.data || [];
  const barGraphData = fetchedBarData?.data || [];

  const chartData = useMemo(() => {
    return type === "comboBar" ? { line: lineGraphData, bar: barGraphData } : graphData;
  }, [type, lineGraphData, barGraphData, graphData]);

  const chartProps = {
    ...data,
    data: chartData,
    height: ref.current?.clientHeight,
    isDark,
  };

  const loading = isGraphFetching || isLineFetching || isBarFetching;

  return (
    <div ref={ref} className="h-100">
      <LoadingOverlay isLoading={loading} className="h-100">
        <div className="ql">
          <div className="ql-editor p-0 pb-1" style={{ textAlign: "center" }}>
            {!isTitleHidden && <div dangerouslySetInnerHTML={{ __html: chartTitle }}></div>}
          </div>
        </div>

        {(graphData?.length > 0 || lineGraphData?.length > 0 || barGraphData?.length > 0) && !loading ? (
          <div
            className="nodrag nopan nowheel"
            style={{ width: "100%", height: "calc(100% - 28px)", position: "relative", cursor: "auto" }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {type === "line" ? (
              chartType === "chartJs" ? (
                <ChartJsLineChart {...chartProps} showThumbsUp={showThumbsUp} />
              ) : (
                <SplineChart {...chartProps} showThumbsUp={showThumbsUp} />
              )
            ) : type === "bar" ? (
              chartType === "chartJs" ? (
                <ChartJsBarChart {...chartProps} showThumbsUp={showThumbsUp} />
              ) : (
                <CanvasjsBarChart {...chartProps} showThumbsUp={showThumbsUp} />
              )
            ) : type === "pie" ? (
              <CanvasjsPieChart {...chartProps} />
            ) : type === "comboBar" ? (
              chartType === "chartJs" ? (
                <ChartJsBarLineCombo {...chartProps} showThumbsUp={showThumbsUp} />
              ) : (
                <CanvasjsBarLineComboChart {...chartProps} showThumbsUp={showThumbsUp} />
              )
            ) : (
              ""
            )}
          </div>
        ) : (
          <h2 className="delayed-message">No data found!</h2>
        )}
      </LoadingOverlay>
    </div>
  );
});

export default ChartFetch;

ChartFetch.displayName = "ChartFetch";

ChartFetch.propTypes = {
  type: PropTypes.string.isRequired,
  data: PropTypes.object,
  isSelected: PropTypes.bool,
  isDark: PropTypes.bool,
  mainDashboard: PropTypes.bool,
  dashboardName: PropTypes.string,
};
